import type { MealType } from '@fitness/api-client';
import { defaultMeal } from '@fitness/core';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, PackageSearch, Plus } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/Button';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { BarcodeScanner } from '@/components/food/BarcodeScanner';
import { QuantitySheet } from '@/components/food/QuantitySheet';
import { foodAddSchema, type FoodAddSearch } from '@/components/food/searchParams';
import { todayIso } from '@/components/food/format';
import { describeError, hasErrorCode } from '@/lib/api';
import { readLastMeal } from '@/lib/food/mealPreference';
import { useBarcodeLookup } from '@/lib/queries/foods';

export const Route = createFileRoute('/_app/food/scan')({
  validateSearch: (search: Record<string, unknown>): FoodAddSearch => foodAddSchema.parse(search),
  component: ScanPage,
});

/** Un código de barras es una tira de dígitos de 8 a 14 cifras. */
const BARCODE = /^\d{8,14}$/;

/**
 * Escáner de códigos de barras.
 *
 * Leído el código, se busca el producto: si está (o Open Food Facts lo conoce)
 * se abre directamente la hoja de cantidad; si no, se ofrece crearlo con el
 * código ya puesto; y si la fuente externa no contesta, se dice tal cual en
 * lugar de fingir que el producto no existe.
 */
function ScanPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const date = search.date ?? todayIso();
  const [meal] = useState<MealType>(() =>
    defaultMeal({ date, now: new Date(), queryMeal: search.meal, lastMeal: readLastMeal() }),
  );

  const [code, setCode] = useState('');
  const [manual, setManual] = useState('');
  const lookup = useBarcodeLookup(code, code !== '');

  const notFound = lookup.isError && hasErrorCode(lookup.error, 'not_found');
  const upstreamDown = lookup.isError && hasErrorCode(lookup.error, 'upstream_unavailable');
  const manualError =
    manual !== '' && !BARCODE.test(manual.trim()) ? 'Son de 8 a 14 dígitos.' : undefined;

  return (
    <>
      <PageHeader
        title="Escanear"
        subtitle="Apunta al código de barras del producto."
        action={
          <Link
            to="/food/add"
            search={{ date, meal }}
            aria-label="Volver"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors hover:bg-surface-raised active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <div className="flex flex-col gap-4">
        <BarcodeScanner paused={code !== ''} onDetected={setCode} />

        {code !== '' ? (
          <div className="rounded-2xl border border-line bg-surface-raised p-4">
            <p className="text-xs text-ink-faint">Código leído</p>
            <p className="font-mono text-lg text-ink tabular-nums">{code}</p>

            {lookup.isPending ? (
              <div className="mt-3 flex items-center gap-3 text-ink-muted">
                <Spinner size="sm" />
                <span className="text-sm">Buscando el producto…</span>
              </div>
            ) : notFound ? (
              <div className="mt-3 flex flex-col gap-3">
                <p className="flex items-center gap-2 text-sm text-ink-muted">
                  <PackageSearch className="size-4 shrink-0" aria-hidden="true" />
                  No encontramos este producto.
                </p>
                <Button
                  icon={<Plus className="size-4" />}
                  onClick={() =>
                    void navigate({
                      to: '/food/foods/new',
                      search: { barcode: code, date, meal },
                    })
                  }
                >
                  Crear alimento
                </Button>
              </div>
            ) : upstreamDown ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                Open Food Facts no responde, prueba más tarde.
              </p>
            ) : lookup.isError ? (
              <p role="alert" className="mt-3 text-sm text-danger">
                {describeError(lookup.error)}
              </p>
            ) : null}

            <Button
              variant="ghost"
              className="mt-3"
              onClick={() => {
                setCode('');
                setManual('');
              }}
            >
              Escanear otro
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-col gap-3 rounded-2xl border border-line bg-surface-raised p-4"
            onSubmit={(event) => {
              event.preventDefault();
              const trimmed = manual.trim();
              if (BARCODE.test(trimmed)) setCode(trimmed);
            }}
          >
            <Field
              htmlFor="barcode"
              label="…o escribe el código a mano"
              error={manualError}
              hint="Útil si la cámara no está disponible o el código está estropeado."
            >
              <Input
                {...fieldAria('barcode', manualError, true)}
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={manual}
                onChange={(event) => setManual(event.target.value)}
                placeholder="3017624010701"
                maxLength={14}
              />
            </Field>
            <Button type="submit" block disabled={!BARCODE.test(manual.trim())}>
              Buscar producto
            </Button>
          </form>
        )}
      </div>

      {lookup.data ? (
        <QuantitySheet
          open
          onClose={() => setCode('')}
          target={{ kind: 'food', food: lookup.data }}
          date={date}
          meal={meal}
          mealLocked
          onSaved={() => void navigate({ to: '/food', search: { date } })}
        />
      ) : null}
    </>
  );
}
